import {
  contentLang,
  formatOdds,
  resolveApiBase,
  SportsApiClient,
  type OddsFormat,
  type WidgetProps,
} from '@sports/core'
import { useState } from 'preact/hooks'
import { useFeed } from '../../use-feed'
import { useLabels } from '../../use-labels'

export const OUTRIGHT_ATTRIBUTES = [
  'market',
  'lang',
  'context',
  'heading',
  'refresh',
  'api-base',
  'theme',
] as const

interface OutrightProps extends WidgetProps {
  market?: string
  lang?: string
  context?: string
  heading?: string
  refresh?: string
  apiBase?: string
}

export const outrightStyles = `
  .sports-card.outright > .sports-head { display: none; }
  /* Shared column template so the header labels sit exactly above their column:
     selection (left) · odds (centered) · bookmaker (right). */
  .outhead, .out-row { display: grid; grid-template-columns: 1fr minmax(4rem, auto) 5rem; align-items: center; gap: .75rem; }
  .outhead {
    padding: .8rem 1.1rem; background: var(--sports-header); color: #e8b84b;
    font-weight: 700; font-style: italic; font-size: .9rem;
  }
  .outhead .l { text-align: left; }
  .outhead .r { text-align: center; display: flex; gap: .4rem; justify-content: center; }
  .outhead .r .col { min-width: 3.4rem; text-align: center; }
  /* Slate sub-header carrying the market name. */
  .submarket {
    text-align: center; padding: .5rem 1rem; color: #e8b84b; font-size: .8rem; font-style: italic;
    background: color-mix(in srgb, var(--sports-header) 62%, var(--sports-card));
  }
  .out-row {
    padding: .6rem 1.1rem; border-top: 1px solid var(--sports-border); font-size: .92rem;
  }
  .out-row .name { display: flex; align-items: center; justify-content: flex-start; gap: .5rem; min-width: 0; }
  .out-row .name span { overflow: hidden; text-overflow: ellipsis; white-space: nowrap; }
  .out-row .name img { width: 20px; height: 20px; object-fit: contain; flex: none; }
  .prices { display: flex; gap: .4rem; justify-content: center; }
  .out-row .bk { display: flex; align-items: center; justify-content: flex-end; }
  .out-row .bk img { height: 15px; max-width: 82px; object-fit: contain; }
  .pill {
    min-width: 3.4rem; text-align: center; padding: .28rem .6rem; border-radius: 8px;
    background: color-mix(in srgb, var(--sports-fg) 9%, transparent); font-weight: 700;
    font-variant-numeric: tabular-nums; text-decoration: none; color: var(--sports-fg);
  }
  .pill.best { background: #fde68a; color: #111318; }
  .pill.empty { color: var(--sports-muted); background: transparent; font-weight: 400; }
  .see-more {
    display: block; width: 100%; padding: .6rem 1rem; text-align: center;
    background: var(--sports-card); color: var(--sports-muted);
    border: none; border-top: 1px solid var(--sports-border);
    font: inherit; font-size: .82rem; cursor: pointer;
  }
  .see-more:hover { color: var(--sports-fg); }
`

interface OutrightResponse {
  key: string
  label: string
  sport?: string | null
  marketType?: string
  bookmakers: string[]
  multi: boolean
  rows: (Record<string, number | string> & { name: string })[]
  // The API serves the last-good snapshot when the live source is empty (a
  // closed/expired market). Those prices are indicative, not live — we must not
  // present them as freshly updated.
  stale?: boolean
}

interface BookmakerResponse {
  oddsFormat: OddsFormat
  brand: { name?: string; logo?: string | null; link?: string | null } | null
}

function number(value: string | undefined, fallback: number): number {
  const parsed = Number(value)
  return Number.isFinite(parsed) ? parsed : fallback
}

/**
 * One outright table in the betbrothers.es style: a navy header with gold column
 * labels, a slate sub-header carrying the market name, name + odds-pill rows, and
 * an "updated" footer. No widget title — the host page provides the H2. Single
 * source → one price column; several sources → one column per bookmaker with the
 * best (lowest) price highlighted.
 */
export function Outright({
  market = '',
  lang = 'es',
  context = 'outright',
  refresh,
  apiBase,
}: OutrightProps) {
  const base = resolveApiBase(apiBase)
  const refreshSeconds = number(refresh, 300)
  const client = new SportsApiClient(base)
  const labels = useLabels(base, lang)
  // Until /v1/ui answers (or if it fails), show the page's own language, never
  // Spanish on an English page.
  const es = contentLang(lang) === 'es'
  const t = (key: string, spanish: string, english: string) => labels(key, es ? spanish : english)
  const [expanded, setExpanded] = useState(false)

  const { data, error, loading } = useFeed(
    (signal) => client.outright<OutrightResponse>(market, lang, signal),
    [base, market, lang],
    refreshSeconds,
  )
  const { data: display } = useFeed(
    (signal) => client.bookmaker<BookmakerResponse>(lang, context, signal),
    [base, lang, context],
    0,
  )

  const format: OddsFormat = display?.oddsFormat ?? 'decimal'
  const brand = display?.brand ?? null
  const bookmakers = data?.bookmakers ?? []
  const rows = data?.rows ?? []
  // Show the first 10 rows by default; the "Ver más" footer reveals the rest.
  const visible = expanded ? rows : rows.slice(0, 10)
  // "Equipo" for team markets, "Jugador" for scorer/MVP markets.
  const isPlayer = /goleador|scorer|mvp|pichichi|jugador|bal[oó]n|ballon/i.test(`${data?.marketType ?? ''} ${market}`)
  const entityLabel = isPlayer ? t('player', 'Jugador', 'Player') : t('team', 'Equipo', 'Team')

  const priceOf = (row: Record<string, number | string>, book: string): number => {
    const value = row[book]
    return typeof value === 'number' ? value : Number(value)
  }
  // Best odds for the bettor = the HIGHEST price across bookmakers (biggest
  // payout) — highlighted yellow.
  const bestFor = (row: Record<string, number | string>): number =>
    Math.max(...bookmakers.map((b) => priceOf(row, b)).filter((p) => Number.isFinite(p) && p > 1))

  const link = brand?.link ?? undefined
  // The per-locale bookmaker at the right of every priced row — links to the
  // affiliate URL, like the odds pills.
  const bookmaker = () => {
    const inner = brand?.logo ? (
      <img src={brand.logo} alt={brand.name ?? ''} />
    ) : (
      brand?.name ?? ''
    )
    if (!brand?.logo && !brand?.name) return <span class="bk" />
    return link ? (
      <a class="bk" href={link} target="_blank" rel="sponsored noopener">
        {inner}
      </a>
    ) : (
      <span class="bk">{inner}</span>
    )
  }
  const pill = (value: number, best: boolean) => {
    const text = Number.isFinite(value) && value > 1 ? formatOdds(value, format) : ''
    const cls = `pill${best ? ' best' : ''}${text ? '' : ' empty'}`
    return link && text ? (
      <a class={cls} href={link} target="_blank" rel="sponsored noopener">
        {text}
      </a>
    ) : (
      <span class={cls}>{text || '—'}</span>
    )
  }

  return (
    <div class="sports-card outright">
      <div class="sports-head" />

      <div class="outhead">
        <span class="l">{entityLabel}</span>
        {data?.multi ? (
          <span class="r">
            {bookmakers.map((b) => (
              <span class="col" key={b}>
                {b}
              </span>
            ))}
          </span>
        ) : (
          <span class="r">{t('odds', 'Cuotas', 'Odds')}</span>
        )}
        <span class="bkcol" />
      </div>

      {data?.label && rows.length > 0 && <div class="submarket">{data.label}</div>}

      {loading && <div class="sports-note">{t('loading', 'Cargando…', 'Loading…')}</div>}
      {!loading && error && (
        <div class="sports-note error">{t('oddsUnavailable', 'Cuotas no disponibles.', 'Odds are unavailable right now.')}</div>
      )}
      {!loading && !error && rows.length === 0 && (
        <div class="sports-note">{t('noOdds', 'Sin cuotas disponibles.', 'No odds available.')}</div>
      )}

      {visible.map((row) => {
        const best = data?.multi ? bestFor(row) : NaN
        return (
          <div class="out-row" key={row.name}>
            <span class="name">
              {typeof row.logo === 'string' && (
                <img src={row.logo} alt="" onError={(e) => ((e.target as HTMLImageElement).style.display = 'none')} />
              )}
              <span>{row.name}</span>
            </span>
            <span class="prices">
              {data?.multi
                ? bookmakers.map((book) => {
                    const price = priceOf(row, book)
                    return <span key={book}>{pill(price, price === best)}</span>
                  })
                : pill(priceOf(row, bookmakers[0]), false)}
            </span>
            {bookmaker()}
          </div>
        )
      })}

      {rows.length > 10 && (
        <button class="see-more" type="button" onClick={() => setExpanded(!expanded)}>
          {expanded ? t('seeLess', 'Ver menos', 'See less') : `${t('seeMore', 'Ver más', 'See more')} (${rows.length})`}
        </button>
      )}
    </div>
  )
}
